import React from 'react';
import { ArrowSquareOut, X } from '@phosphor-icons/react';
import { Flex, Text, Button, StatusTimeline, TagBadge, type TagColor } from '@chatsdet/ui';
import { formatRelativeTimeAgo } from '../../../features/workflows/useTestRunDetail';
import { buildRunTimelineEntries } from '../../../features/workflows/runTimeline';
import type { TestSuiteMemberDetail } from '../../../features/test-suites';

interface SuiteTestCardProps {
  member: TestSuiteMemberDetail;
  /** URL to open the flow in the designer (new tab). */
  flowHref: string;
  onRemove: () => void;
  /** When true the card is the drag overlay — disables interactive affordances. */
  isOverlay?: boolean;
}

/**
 * A rich workflow card shown inside a suite layer band: title, last-10-runs
 * strip (same component as the flows table), last run time, and tags. The card
 * body is the drag handle (wired by BoardCard); the link-out and remove controls
 * are non-drag hot zones.
 */
export function SuiteTestCard({ member, flowHref, onRemove, isOverlay }: SuiteTestCardProps) {
  const entries = React.useMemo(
    () => buildRunTimelineEntries(member.recentRuns ?? [], member.workflowName),
    [member.recentRuns, member.workflowName]
  );

  return (
    <div
      className={`flex w-72 shrink-0 flex-col gap-3 rounded-lg border border-border bg-card p-3 shadow-sm transition-shadow ${
        isOverlay ? 'shadow-md' : 'hover:shadow-md'
      }`}
    >
      <Flex align="start" justify="between" gap="xs">
        <Text weight="semibold" size="sm" className="line-clamp-2 flex-1 pt-1">
          {member.workflowName}
        </Text>
        {/* Controls are non-drag hot zones: stop mousedown/pointerdown from
            reaching the card's drag listeners while keeping the buttons clickable. */}
        <div
          className="flex shrink-0 items-center gap-0.5"
          onPointerDown={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <Button
            variant="icon"
            size="sm"
            iconOnly
            tooltip="Open flow in new tab"
            onPress={() => window.open(flowHref, '_blank', 'noopener,noreferrer')}
          >
            <ArrowSquareOut size={15} />
          </Button>
          <Button
            variant="icon"
            size="sm"
            iconOnly
            tooltip="Remove from suite"
            aria-label={`Remove ${member.workflowName} from suite`}
            onPress={onRemove}
          >
            <X size={15} />
          </Button>
        </div>
      </Flex>

      {entries.length > 0 ? (
        <StatusTimeline entries={entries} size="sm" showNavigation={false} />
      ) : (
        <Text size="xs" className="text-muted-foreground/50">
          No runs yet
        </Text>
      )}

      <Flex align="center" justify="between" gap="xs">
        <Text size="xs" className="text-muted-foreground">
          {member.lastRunAt ? `Last run ${formatRelativeTimeAgo(member.lastRunAt)}` : 'Never run'}
        </Text>
      </Flex>

      {member.tags && member.tags.length > 0 && (
        <Flex align="center" gap="xs" wrap>
          {member.tags.map((tag) => (
            <TagBadge key={tag.id} name={tag.name} color={tag.color as TagColor} size="sm" />
          ))}
        </Flex>
      )}
    </div>
  );
}
